import React, { useCallback } from 'react';
import {
  IncidentHospital,
  useIncidentHospitals,
  useCreateIncidentHospital,
  useEditIncidentHospital,
} from 'components/IAP/hooks/incidentHospitalsHooks';
import { HospitalBase } from 'types/iapBaseTypes';
import { MedicalPlanTable, FieldDescriptor } from './MedicalPlanTable';

const FIELDS: FieldDescriptor[] = [
  {
    type: 'text',
    accessorKey: 'hospital_name',
    header: 'Hospital',
    placeholder: 'Hospital Name *',
    sortable: true,
    maxLength: 200,
  },
  {
    type: 'text',
    accessorKey: 'address',
    header: 'Address',
    placeholder: 'Address',
  },
  {
    type: 'text',
    accessorKey: 'contact_number',
    header: 'Contact',
    placeholder: 'Contact',
    maxLength: 40,
  },
  {
    type: 'text',
    accessorKey: 'travel_time_air',
    header: 'Travel (Air)',
    placeholder: 'e.g. 15 min',
    maxLength: 50,
  },
  {
    type: 'text',
    accessorKey: 'travel_time_ground',
    header: 'Travel (Ground)',
    placeholder: 'e.g. 30 min',
    maxLength: 50,
  },
  {
    type: 'boolean',
    accessorKey: 'trauma_center',
    header: 'Trauma',
    ariaLabel: 'Trauma center',
  },
  {
    type: 'text',
    accessorKey: 'trauma_center_level',
    header: 'Trauma Level',
    placeholder: 'e.g. Level I',
    maxLength: 20,
  },
  {
    type: 'boolean',
    accessorKey: 'burn_center',
    header: 'Burn Center',
    ariaLabel: 'Burn center',
  },
  {
    type: 'boolean',
    accessorKey: 'heli_pad',
    header: 'Heli Pad',
    ariaLabel: 'Heli pad',
  },
];

const EMPTY_DRAFT = {
  hospital_name: '',
  address: '',
  contact_number: '',
  travel_time_air: '',
  travel_time_ground: '',
  trauma_center: false,
  trauma_center_level: '',
  burn_center: false,
  heli_pad: false,
};

const toEditPayload = (
  entity: IncidentHospital,
  override: Record<string, string | boolean>
): HospitalBase & { id: string; hospital_id: string; archived: boolean } => ({
  id: entity.id,
  hospital_id: entity.hospital_id,
  hospital_name: entity.hospital_name,
  description: entity.description,
  address: entity.address,
  contact_number: entity.contact_number,
  travel_time_air: entity.travel_time_air,
  travel_time_ground: entity.travel_time_ground,
  burn_center: entity.burn_center,
  heli_pad: entity.heli_pad,
  trauma_center: entity.trauma_center,
  trauma_center_level: entity.trauma_center_level,
  archived: entity.archived,
  ...override,
});

type Props = { readOnly?: boolean };

export const HospitalsTable: React.FC<Props> = ({ readOnly = false }) => {
  const { data: incidentHospitals } = useIncidentHospitals('incident');
  const activeIncidentHospitals = (incidentHospitals ?? []).filter(
    (e) => !e.archived
  );

  const { mutate: createIncidentHospital, isPending: isCreating } =
    useCreateIncidentHospital();
  const { mutate: editIncidentHospital } = useEditIncidentHospital();

  const onSaveDraft = useCallback(
    (draft: Record<string, string | boolean>, onSuccess: () => void) => {
      createIncidentHospital(
        {
          hospital_name: draft.hospital_name as string,
          address: draft.address as string,
          contact_number: draft.contact_number as string,
          travel_time_air: draft.travel_time_air as string,
          travel_time_ground: draft.travel_time_ground as string,
          trauma_center: draft.trauma_center as boolean,
          trauma_center_level: draft.trauma_center_level as string,
          burn_center: draft.burn_center as boolean,
          heli_pad: draft.heli_pad as boolean,
        },
        { onSuccess }
      );
    },
    [createIncidentHospital]
  );

  const onSaveField = useCallback(
    (entity: IncidentHospital, field: string, value: string | boolean) => {
      editIncidentHospital(toEditPayload(entity, { [field]: value }));
    },
    [editIncidentHospital]
  );

  const onDelete = useCallback(
    (entity: IncidentHospital) => {
      editIncidentHospital(toEditPayload(entity, { archived: true }));
    },
    [editIncidentHospital]
  );

  return (
    <MedicalPlanTable
      title="Hospitals"
      addButtonLabel="Add Hospital"
      noDataMessage="No hospitals defined. Click Add Hospital to get started."
      entities={activeIncidentHospitals}
      emptyDraft={EMPTY_DRAFT}
      requiredField="hospital_name"
      fields={FIELDS}
      isCreating={isCreating}
      onSaveDraft={onSaveDraft}
      onSaveField={onSaveField}
      onDelete={onDelete}
      readOnly={readOnly}
    />
  );
};
